import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, Observer, of, Subject} from 'rxjs';
import {tap} from 'rxjs/operators';

import {StaticDataService} from '../../services/static-data.service';
import {FormMasterItemConfigurationModel} from '../../models/forms/form-master-item-configuration.model';
import {EnumValidValueItemModel} from '../../models/enum/enum-valid-value-item.model';
import {EnumValidValueModel} from '../../models/enum/enum-valid-value.model';
import {FormItemConfigurationModel} from '../../models/forms/form-item-configuration.model';
import {FormResultDataModel} from '../../models/forms/data/form-result-data.model';
import {PatientCaseWorkItemFormDataModel} from '../../models/forms/data/patient-case-work-item-form-data.model';
import {FormParagraphDataModel} from '../../models/forms/data/form-paragraph-data.model';
import {FormParagraphConfigurationModel} from '../../models/forms/form-paragraph-configuration.model';
import {FormVersionConfigurationModel} from '../../models/forms/form-version-configuration.model';
import {FormConfigurationModel} from '../../models/forms/form-configuration.model';
import {FormItemActionConfigurationModel} from '../../models/forms/form-item-action-configuration.model';
import {FormItemCalculationConfigurationModel} from '../../models/forms/form-item-calculation-configuration.model';
import {UserService} from '../../services/user.service';
import { MessagingService } from 'src/app/services/messaging.service';

@Injectable({
  providedIn: 'root'
})
export class FormsService {
  //formUpdatedSubject: Subject<PatientCaseWorkItemFormDataModel>;
  currentForm: PatientCaseWorkItemFormDataModel;

  constructor(
    private http: HttpClient, private staticDataService: StaticDataService, private userService: UserService,
    private messagingService: MessagingService
  ) {
    //this.formUpdatedSubject = new Subject<PatientCaseWorkItemFormDataModel>();
  }

  getNewFormGuids(): Observable<string[]> {
    return this.http.get<string[]>('api/forms/getNewFormGuids', UserService.authorizationHeaderObject);
  }

  getMedicalInformationFormForPatient(patientId: string) {
    /*const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    };*/
    return this.http.get<any>(`api/forms/getMedicalInformationFormForPatient/${patientId}`);
  }

  getDataForm(CaseWorkItemFormsId: string): Observable<PatientCaseWorkItemFormDataModel> {
    return this.http.get<PatientCaseWorkItemFormDataModel>(
      `/api/forms/get-data-form/${CaseWorkItemFormsId}`, UserService.authorizationHeaderObject)
      .pipe(
        tap((dataForm) => {
          if (this.staticDataService.isReady) {
            this.processGetDataFormResponse(dataForm);
          } else {
            this.staticDataService.validValuesReadyEvent.subscribe({
              complete: () => {
                this.processGetDataFormResponse(dataForm);
              }
            });
          }
        })
      );
  }

  processGetDataFormResponse(dataForm:PatientCaseWorkItemFormDataModel){
    // console.log('FormsService processGetDataFormResponse', dataForm);

    for (const dataFormParagraph of dataForm.dataFormParagraphs) {
      this.addStaticFormData(dataFormParagraph);
    }
    this.currentForm = dataForm;
    //console.log('FormsService processGetDataFormResponse formUpdatedSubject');
    //this.formUpdatedSubject.next(this.currentForm);

  }

  getOrganizationInsuranceCard(OrganizationID): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    };
    return this.http.get<any>(`/api/forms/getOrganizationInsuranceCard/${OrganizationID}`, httpOptions);
  }

  getAllAvailableInsuranceCardForms(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    };
    return this.http.get<any>('/api/forms/getAllAvailableInsuranceCardForms', httpOptions);
  }

  saveOrganizationForm(organizationForm: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    };
    return this.http.post<any>('/api/forms/saveOrganizationForm', organizationForm, httpOptions);
  }

  getCfgDForm(DFormId: string): Observable<FormConfigurationModel> {
    return this.http.get<FormConfigurationModel>(`/api/forms/getCfgDForm/${DFormId}`, UserService.authorizationHeaderObject);
  }


  createSpoofDataForm(cfgVersion: FormVersionConfigurationModel): Observable<PatientCaseWorkItemFormDataModel> {
    return this.http.post<PatientCaseWorkItemFormDataModel>('api/forms/createSpoofDataForm', cfgVersion, UserService.authorizationHeaderObject);
  }

  getInsuranceCardFormID(organizationID: string, patientID: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    };
    return this.http.get<any>(`/api/forms/getInsuranceCardFormID/${organizationID}/${patientID}`);
  }

  saveResult(result: FormResultDataModel): Observable<FormResultDataModel> {
    return this.http.post<FormResultDataModel>(`/api/forms/saveResult`, result);
  }

  deleteResult(resultId: string) {
    return this.http.get<any>(`/api/forms/delete-result/${resultId}`);
  }

  duplicateParagraph(duplicateObj: any): Observable<FormParagraphDataModel> {
    return this.http.post<FormParagraphDataModel>(
      '/api/forms/duplicate-paragraph',
      duplicateObj, UserService.authorizationHeaderObject
    ).pipe(
      tap((dataFormParagraph:FormParagraphDataModel) => {
        // console.log('FormsService duplicateParagraph', dataFormParagraph);

        this.addStaticFormData(dataFormParagraph);
        this.currentForm.dataFormParagraphs.push(dataFormParagraph);
        // console.log('FormsService processGetDataFormResponse duplicateParagraph action:');
        //this.formUpdatedSubject.next(this.currentForm);
      })
    );
  }

  deleteParagraph(dataFormParagraphId: string): Observable<FormParagraphDataModel> {
    return this.http.delete<FormParagraphDataModel>(`/api/forms/dataformparagraph/${dataFormParagraphId}`
    ).pipe(
      tap((dataFormParagraph) => {
        // console.log('FormsService deleteParagraph', dataFormParagraph);
        const paragraphIndex = this.currentForm.dataFormParagraphs.findIndex( p=>p.dataFormParagraphID === dataFormParagraph.dataFormParagraphID);
        const paragraphToDelete = this.currentForm.dataFormParagraphs[paragraphIndex];
        const requiredItems = paragraphToDelete.dataFormItems.filter(i => i.cfgDFormItem.isRequired);
        requiredItems.forEach(element => {
                  this.messagingService.sendComponentMessage(`FORMS_ITEM_REQUIRED_${paragraphToDelete.CaseWorkItemFormsId}`, { required: false, dataFormItemId: element.dataFormItemId });

        });
        // console.log('FormsService deleteParagraph paragraphToDelete', paragraphToDelete);
        this.currentForm.dataFormParagraphs.splice(paragraphIndex, 1);
        // console.log('FormsService processGetDataFormResponse deleteParagraph');
        //this.formUpdatedSubject.next(this.currentForm);        
      })
    );
  }

  saveCfgForm(cfgForm: any, saveMode: string): Observable<any> {
    return this.http.post<any>(`/api/forms/saveCfgForm/${saveMode}`, cfgForm, UserService.authorizationHeaderObject);
  }

  saveCfgFormVersion(cfgFormVersion: any, saveMode: string): Observable<any> {
    return this.http.post<any>(`/api/forms/saveCfgFormVersion/${saveMode}`, cfgFormVersion, UserService.authorizationHeaderObject);
  }

  saveCfgParagraph(cfgParagraph: any, saveMode: string): Observable<any> {
    return this.http.post<any>(`/api/forms/saveCfgParagraph/${saveMode}`, cfgParagraph, UserService.authorizationHeaderObject);
  }

  saveCfgItem(cfgItem: any, saveMode: string): Observable<any> {
    return this.http.post<any>(`/api/forms/saveCfgItem/${saveMode}`, cfgItem, UserService.authorizationHeaderObject);
  }

  saveCfgMasterItem(cfgMasterItem: any, saveMode: string): Observable<any> {
    return this.http.post<any>(`/api/forms/saveCfgMasterItem/${saveMode}`, cfgMasterItem, UserService.authorizationHeaderObject);
  }

  gatherCfgFormAsync(DFormId: string) {
    let promise = new Promise((resolve, reject) => {
      let formAndParagraphPromises = [
        this.fetchCfgFormAndDetails(DFormId),
        this.fetchCfgVersions(DFormId),
      ];

      Promise.all(formAndParagraphPromises).then((results: any[]) => {
        let cfgForm: FormConfigurationModel = results[0];
        cfgForm.cfgDFormVersions = results[1];
        resolve(cfgForm);
      }, err => {
        reject(err);
      });
    });
    return promise;
  }

  getCfgVersionsSolo(DFormId: string): Observable<FormVersionConfigurationModel[]> {
    return this.http.get<FormVersionConfigurationModel[]>(`/api/forms/getCfgVersionsSolo/${DFormId}`, UserService.authorizationHeaderObject);
  }

  getValidValueItemSolo(validValueItemId: string): Observable<EnumValidValueItemModel> {
    return this.http.get<EnumValidValueItemModel>(`/api/forms/getValidValueItemSolo/${validValueItemId}`, UserService.authorizationHeaderObject);
  }

  getCfgMasterItemSolo(masterItemId: string): Observable<FormMasterItemConfigurationModel> {
    return this.http.get<FormMasterItemConfigurationModel>(`/api/forms/getCfgMasterItemSolo/${masterItemId}`, UserService.authorizationHeaderObject);
  }

  getCfgCalculationSolo(calculationId: string): Observable<FormItemCalculationConfigurationModel> {

    return this.http.get<FormItemCalculationConfigurationModel>(`/api/forms/getCfgCalculationSolo/${calculationId}`, UserService.authorizationHeaderObject);
  }

  getCfgActionSolo(actionId: string): Observable<FormItemActionConfigurationModel> {
    return this.http.get<FormItemActionConfigurationModel>(`/api/forms/getCfgActionSolo/${actionId}`, UserService.authorizationHeaderObject);
  }

  getEnumValidValueSolo(validValueId: string): Observable<EnumValidValueModel> {
    return this.http.get<EnumValidValueModel>(`/api/forms/getEnumValidValueSolo/${validValueId}`, UserService.authorizationHeaderObject);
  }

  getCfgItemsSoloFromParent(cfgParentId: string): Observable<FormItemConfigurationModel[]> {
    return this.http.get<FormItemConfigurationModel[]>(`/api/forms/getCfgItemsSoloFromParent/${cfgParentId}`, UserService.authorizationHeaderObject);
  }

  getCfgItemsSolo(cfgParagraphId: string): Observable<FormItemConfigurationModel[]> {
    return this.http.get<FormItemConfigurationModel[]>(`/api/forms/getCfgItemsSolo/${cfgParagraphId}`, UserService.authorizationHeaderObject);
  }

  getCfgItemSolo(cfgItemId: string): Observable<FormItemConfigurationModel> {
    return this.http.get<FormItemConfigurationModel>(`/api/forms/getCfgItemSolo/${cfgItemId}`, UserService.authorizationHeaderObject);
  }

  getCfgParagraphsSolo(cfgVersionId: string): Observable<FormParagraphConfigurationModel[]> {
    return this.http.get<FormParagraphConfigurationModel[]>(`/api/forms/getCfgParagraphsSolo/${cfgVersionId}`, UserService.authorizationHeaderObject);
  }

  getCfgDFormSolo(DFormId: string): Observable<FormConfigurationModel> {
    return this.http.get<FormConfigurationModel>(`/api/forms/getCfgDFormSolo/${DFormId}`, UserService.authorizationHeaderObject);
  }

  completeForm(DFormId: string, CaseWorkItemFormsId: string): Observable<FormConfigurationModel> {
    return this.http.post<FormConfigurationModel>(`/api/forms/${DFormId}/complete/${CaseWorkItemFormsId}`, {}, UserService.authorizationHeaderObject);
  }
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any) => {
      console.error(error);
      return of(result as T);
    };
  }

  private fetchCfgFormAndDetails(DFormId: string) {
    let promise = new Promise((resolve, reject) => {
      this.getCfgDFormSolo(DFormId)
        .subscribe((cfgDForm: FormConfigurationModel) => {
          let enumPromises = [
            this.fetchEnumValidValueItem(cfgDForm.enumFormCategoryID),
            this.fetchEnumValidValueItem(cfgDForm.enumFormDataSourceID),
            this.fetchEnumValidValueItem(cfgDForm.enumCustomFormID),
          ];
          Promise.all(enumPromises).then((validValues: EnumValidValueItemModel[]) => {
            cfgDForm.enumFormCategory = validValues[0];
            cfgDForm.enumFormDataSource = validValues[1];
            cfgDForm.enumCustomForm = validValues[2];
            resolve(cfgDForm);
          }, err => {
            reject(err);
          });
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  private fetchCfgVersions(DFormId: string) {
    let promise = new Promise((resolve, reject) => {
      this.getCfgVersionsSolo(DFormId).subscribe((versions: FormVersionConfigurationModel[]) => {
        let versionPromises = versions.map(version => {
          return this.fetchCfgVersionDetails(version);
        });
        Promise.all(versionPromises).then(results => {
          resolve(results);
        }, err => {
          reject(err);
        });
      }, err => {
        reject(err);
      });
    });
    return promise;
  }

  private fetchCfgVersionDetails(version: FormVersionConfigurationModel) {
    let promise = new Promise((resolve, reject) => {
      let promises = [
        this.fetchEnumValidValueItem(version.enumDFormBuildStatusID),
        this.fetchCfgParagraphs(version.DFormVersionId),
      ];
      Promise.all(promises).then((results: any[]) => {
        version.enumDFormBuildStatus = results[0];
        version.cfgDFormParagraphs = results[1];
        resolve(version);
      }, err => {
        reject(err);
      });
    });
    return promise;
  }

  private fetchCfgParagraphs(cfgVersionId: string) {
    let promise = new Promise((resolve, reject) => {
      this.getCfgParagraphsSolo(cfgVersionId)
        .subscribe((paragraphs: FormParagraphConfigurationModel[]) => {
          let promises = paragraphs.map(paragraph => {
            return this.fetchSingleCfgParagraphDetails(paragraph);
          });
          Promise.all(promises).then(endParagraphs => {
            resolve(endParagraphs);
          }, err => {
            reject(err);
          });
        });
    });
    return promise;
  }

  private fetchSingleCfgParagraphDetails(paragraph: FormParagraphConfigurationModel) {
    let promise = new Promise((resolve, reject) => {
      let paragraphPromises = [
        this.fetchCfgSingleItem(paragraph.RepeatDFormItemId),
        this.fetchCfgItems(paragraph.DFormParagraphId),
        this.fetchEnumValidValueItem(paragraph.enumParagraphOrientationID),
        this.fetchEnumValidValueItem(paragraph.enumItemLayoutID),
      ];
      Promise.all(paragraphPromises)
        .then((results: any[]) => {
          paragraph.RepeatDFormItem = results[0];
          paragraph.cfgDFormItems = results[1];
          paragraph.enumParagraphOrientation = results[2];
          paragraph.enumItemLayout = results[3];
          resolve(paragraph);
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  private fetchCfgItems(cfgParagraphId: string) {
    let promise = new Promise((resolve, reject) => {
      this.getCfgItemsSolo(cfgParagraphId)
        .subscribe((cfgItems: FormItemConfigurationModel[]) => {
          let itemPromises = cfgItems.map(item => {
            return this.fetchSingleCfgItemDetails(item);
          });
          Promise.all(itemPromises)
            .then(itemResults => {
              resolve(itemResults);
            }, err => {
              reject(err);
            });
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  private fetchCfgSingleItem(cfgItemId: string) {
    let promise = new Promise((resolve, reject) => {
      if (!cfgItemId) {
        resolve(null);
      } else {
        this.getCfgItemSolo(cfgItemId)
          .subscribe((cfgItem: FormItemConfigurationModel) => {
            this.fetchSingleCfgItemDetails(cfgItem)
              .then(finalItem => {
                resolve(finalItem);
              }, err => {
                reject(err);
              });
          }, err => {
            reject(err);
          });
      }
    });
    return promise;
  }

  private fetchSingleCfgItemDetails(cfgItem: FormItemConfigurationModel) {
    let promise = new Promise((resolve, reject) => {
      let itemPromises = [
        this.fetchCfgCalculation(cfgItem.CalculationID),
        this.fetchCfgAction(cfgItem.BeforeDisplay_ItemActionId),
        this.fetchCfgAction(cfgItem.AfterUpdate_ItemActionId),
        this.fetchEnumValidValue(cfgItem.ValidValueId),
        this.fetchCfgItemsFromParent(cfgItem.DFormItemId),
        this.fetchMasterItem(cfgItem.DFormMasterItemId),
        this.fetchEnumValidValueItem(cfgItem.enumFormControlID),
        this.fetchEnumValidValueItem(cfgItem.enumLabelAligmentID),
        this.fetchEnumValidValueItem(cfgItem.enumPostLabelAligmentID),
      ];
      Promise.all(itemPromises).then((results: any[]) => {
        cfgItem.Calculation = results[0];
        cfgItem.BeforeDisplayItemAction = results[1];
        cfgItem.AfterUpdateItemAction = results[2];
        cfgItem.ValidValue = results[3];
        cfgItem.ChildDFormItems = results[4];
        cfgItem.cfgDFormMasterItem = results[5];
        cfgItem.enumFormControl = results[6];
        cfgItem.enumLabelAligment = results[7];
        cfgItem.enumPostLabelAligment = results[8];
        resolve(cfgItem);
      }, err => {
        reject(err);
      });

    });
    return promise;
  }

  private fetchMasterItem(masterItemId: string) {
    let promise = new Promise((resolve, reject) => {
      this.getCfgMasterItemSolo(masterItemId)
        .subscribe((masterItem: FormMasterItemConfigurationModel) => {
          this.fetchEnumValidValueItem(masterItem.enumFormControlID)
            .then((validValueItem: EnumValidValueItemModel) => {
              masterItem.enumFormControl = validValueItem;
              resolve(masterItem);
            }, err => {
              reject(err);
            });
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  private fetchCfgItemsFromParent(parentItemId: string) {
    let promise = new Promise((resolve, reject) => {
      this.getCfgItemsSoloFromParent(parentItemId)
        .subscribe((cfgItems: FormItemConfigurationModel[]) => {
          let promises = cfgItems.map(item => {
            return this.fetchSingleCfgItemDetails(item);
          });
          Promise.all(promises).then((results: any[]) => {
            resolve(results);
          }, err => {
            reject(err);
          });
        });
    });
    return promise;
  }

  private fetchEnumValidValueItem(validValueItemId: string) {
    let promise = new Promise((resolve, reject) => {
      if (!validValueItemId) {
        resolve(null);
      } else {
        this.getValidValueItemSolo(validValueItemId)
          .subscribe(response => {
            resolve(response);
          }, err => {
            reject(err);
          });
      }
    });
    return promise;
  }

  private fetchCfgAction(actionId: string) {
    let promise = new Promise((resolve, reject) => {
      if (!actionId) {
        resolve(null);
      } else {
        this.getCfgActionSolo(actionId)
          .subscribe(response => {
            resolve(response);
          }, err => {
            reject(err);
          });
      }
    });
    return promise;
  }

  private fetchCfgCalculation(calculationId: string) {
    let promise = new Promise((resolve, reject) => {
      if (!calculationId) {
        resolve(null);
      } else {
        this.getCfgCalculationSolo(calculationId)
          .subscribe(response => {
            resolve(response);
          }, err => {
            reject(err);
          });
      }
    });
    return promise;
  }

  private fetchEnumValidValue(validValueId: string) {
    let promise = new Promise((resolve, reject) => {
      if (!validValueId) {
        resolve(null);
      } else {
        this.getEnumValidValueSolo(validValueId)
          .subscribe(response => {
            resolve(response);
          }, err => {
            reject(err);
          });
      }
    });
    return promise;
  }

  // Added this because including this in the backend query was doubling the amount of time it takes to process as well as
  // doubling the size of the response.  Currently adds enumValidValueItems and cfgItemActionSteps
  private addStaticFormData(dataFormParagraph: FormParagraphDataModel) {
    for (const item of dataFormParagraph.dataFormItems) {
      // Insert valid value items
      
      if (item.cfgDFormItem && item.cfgDFormItem.ValidValueId) {
        const valueId = item.cfgDFormItem.ValidValueId.toLowerCase();

        item.cfgDFormItem.ValidValue = this.staticDataService.validValues.find(p => p.ValidValueId.toLowerCase() === valueId);

        if (item.cfgDFormItem.ValidValue) {
          const validItems = this.staticDataService.validValueItems.filter(p => p.ValidValueId?.toLowerCase() === valueId);
          if (validItems) {
            item.cfgDFormItem.ValidValue.enumValidValueItems = validItems;
          }
        }
      }
      // Insert item action steps
      if (item.cfgDFormItem && item.cfgDFormItem.AfterUpdate_ItemActionId) {
        const actionId = item.cfgDFormItem.AfterUpdate_ItemActionId;
        const actions = this.staticDataService.itemActions.find(p => p.ItemActionId.toLowerCase() === actionId.toLowerCase());
        if (actions) {
          item.cfgDFormItem.AfterUpdateItemAction = actions;
        }
      }
    }
  }
}
